import { inject, Injectable } from '@angular/core';
import { injectQuery } from '@ngneat/query';
import { ContextService } from 'app/core/services/context.service';
import { SiteFreightTypes } from 'app/shared/types';
import { minutesToMilliseconds } from 'date-fns';
import { map, Observable, tap } from 'rxjs';
import { CACHE_KEYS } from 'shared/enums/cache-keys';
import { SitesService } from '../sites.service';

@Injectable({
  providedIn: 'root',
})
export class FreightTypeNameMapperService {
  private contextService = inject(ContextService);
  private siteService = inject(SitesService);
  private query = injectQuery();
  private siteFreightTypes: SiteFreightTypes[] = [];

  public getFreightTypes(): Observable<SiteFreightTypes[]> {
    const siteId = this.contextService.siteId.getValue() as string;
    return this.query({
      queryKey: [CACHE_KEYS.FREIGHT_TYPE, siteId] as const,
      queryFn: () => this.siteService.getFreightTypes(siteId),
      staleTime: minutesToMilliseconds(1),
    }).result$.pipe(
      map((result) => result.data?.value.freightTypes ?? []),
      tap((siteFreightTypes) => {
        this.siteFreightTypes = siteFreightTypes;
      }),
    );
  }

  public getFreightTypeName(id: unknown): string | undefined {
    return this.siteFreightTypes.find((ft) => ft.id === id)?.name;
  }

  /**
   * @deprecated Use the new name mapping pipeline instead
   */
  public injectFreightTypeName<T>({
    propertyName,
    idPropertyName,
    items,
  }: {
    propertyName: string;
    idPropertyName: string;
    items: T;
  }): T {
    if (Array.isArray(items)) {
      return items.map((itemValue) => {
        const id = itemValue[idPropertyName as keyof T];
        const locationName = this.siteFreightTypes.find((loc) => loc.id === id)?.name;

        return {
          ...itemValue,
          [propertyName]: locationName,
        };
      }) as T;
    } else {
      return {
        ...items,
        [propertyName]: this.siteFreightTypes.find((siteLoc) => siteLoc.id === items[idPropertyName as keyof T])?.name,
      };
    }
  }
}
